/*
 * sca Sagas
 *
 * @version 1.0.0
 * @author Pietro Bertuccelli
 * @since 20-Jan-2025
 */

import { put, take, call, all, select } from 'redux-saga/effects'
import * as actions from '../actions/sca'
import * as api from 'Api/sca/sca'
import { flowToastMapType } from 'Utility/flowToastMap'
import { doShowToast } from 'Store/actions/toast'
import { nullSafe } from 'Utility/globalMethods'
import { AML_STATUSES } from 'Route/Aml/modules/AmlConstants'
import { doShowPopup, doHidePopup } from 'Store/actions/popup'
import { push } from 'redux-first-history'
import { flowPopupMapType } from 'Utility/flowPopupMap'

const getAdditionalInfo = state => nullSafe(() => state.sca.persistentData.additionalInfo, '')
const getServiceId = state => nullSafe(() => state.sca.persistentData.serviceId, '')
const getServicePayload = state => nullSafe(() => state.sca.persistentData.servicePayload, '')
const getUser = state => nullSafe(() => state.sca.persistentData.user, '')

function * generateOtpSaga () {
  const user = yield select(getUser)
  const serviceId = yield select(getServiceId)
  const servicePayload = yield select(getServicePayload)
  const additionalInfo = yield select(getAdditionalInfo)
  yield put(actions.generateOTP.request({ user, serviceId, servicePayload, additionalInfo }, { loading: true }))
  const { error, response } = yield call(api.generateOtp, { user, serviceId, servicePayload, additionalInfo })

  if (!error) {
    yield put(actions.generateOTP.success(response.data))
    yield put(doShowToast(flowToastMapType.GENERATED_NEW_OTP))
  } else {
    yield put(actions.generateOTP.failure(error, response))
  }
}

function * checkOtpAmlSaga (params) {
  yield put(actions.checkOtpAml.request({}, { loading: true }))
  const { error, response } = yield call(api.checkOtpAml, {})

  if (!error) {
    yield put(actions.checkOtpAml.success(response.data))
    // Reset SCA state after successful completion
    yield put(actions.doResetState())
    if (params.status === AML_STATUSES.COMPLIANT) {
      yield put(doShowPopup(flowPopupMapType.AML_MODAL))
    } else {
      yield put(doHidePopup())
      yield put(push('/aml'))
    }
  } else {
    if (error.status === 409) {
      const title = nullSafe(() => params.title, '')
      const onClose = nullSafe(() => params.onClose, () => { })
      // Save the action to be retried after OTP verification
      yield put(actions.doSaveActionTypeAndPayload(actions.actionTypes.DO_CHECK_OTP_AML, params))
      yield put(actions.checkOtpAml.failure(error, { isScaPopup: true, additionalData: { title, onClose }, ...response }))
    } else if (error.status === 403) {
      yield put(actions.checkOtpAml.failure(error, { hideErrorPopup: false, ...response }))
    } else {
      yield put(actions.checkOtpAml.failure(error, { hideErrorPopup: true, ...response }))
    }
  }
}

function * generateDoubleOtpSaga () {
  const user = yield select(getUser)
  const serviceId = yield select(getServiceId)
  const servicePayload = yield select(getServicePayload)
  const additionalInfo = yield select(getAdditionalInfo)
  yield put(actions.generateDoubleOtp.request({ user, serviceId, servicePayload, additionalInfo }, { loading: true }))
  const { error, response } = yield call(api.generateDoubleOtp, { user, serviceId, servicePayload, additionalInfo })

  if (!error) {
    yield put(actions.generateDoubleOtp.success(response.data))
    yield put(doShowToast(flowToastMapType.GENERATED_NEW_DOUBLE_OTP))
  } else {
    yield put(actions.generateDoubleOtp.failure(error, response))
  }
}
// @generator saga

// @generator saga:watch
function * watchGenerateDoubleOtp () {
  while (yield take(actions.actionTypes.DO_GENERATE_DOUBLE_OTP)) {
    yield call(generateDoubleOtpSaga)
  }
}

function * watchCheckOtpAml () {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_CHECK_OTP_AML)
    yield call(checkOtpAmlSaga, payload)
  }
}

function * watchGenerateOtp () {
  while (yield take(actions.actionTypes.DO_GENERATE_OTP)) {
    yield call(generateOtpSaga)
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function * rootSaga () {
  yield all([
    watchGenerateOtp(),
    watchCheckOtpAml(),
    watchGenerateDoubleOtp()
    // @generator saga:fork
  ])
}
